/**
 * Demo magic-link sign-in. A prospect opens `…/#demo=<key>`; we exchange the key
 * for a Firebase custom token via the `demoLogin` function and sign them straight
 * in — no signup, no password. The function also records the visit (the sales
 * signal) server-side.
 */

import { signInWithCustomToken } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase';

/** Extract the demo key from `#demo=<key>` (or `?demo=<key>`), or null. */
export function getDemoKeyFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const hashMatch = window.location.hash.match(/[#&]demo=([^&]+)/);
  if (hashMatch) return decodeURIComponent(hashMatch[1]);
  const qp = new URLSearchParams(window.location.search).get('demo');
  return qp || null;
}

/** Remove the demo key from the URL so it doesn't linger or re-trigger on refresh. */
function clearDemoKeyFromUrl() {
  const hash = window.location.hash.replace(/([#&])demo=[^&]+/, '$1').replace(/[#&]$/, '');
  const search = new URLSearchParams(window.location.search);
  search.delete('demo');
  const q = search.toString();
  const newUrl = `${window.location.pathname}${q ? `?${q}` : ''}${hash && hash !== '#' ? hash : '#app'}`;
  window.history.replaceState({}, '', newUrl);
}

/**
 * If a demo key is present, sign the visitor in with it. Returns true on success
 * (the app should then show the trainer), false if there was no key or it failed.
 */
export async function consumeDemoLogin(): Promise<boolean> {
  const demoKey = getDemoKeyFromUrl();
  if (!demoKey) return false;
  try {
    const fn = httpsCallable<{ demoKey: string }, { token: string }>(functions, 'demoLogin');
    const { data } = await fn({ demoKey });
    if (!data?.token) throw new Error('No token returned');
    await signInWithCustomToken(auth, data.token);
    clearDemoKeyFromUrl();
    return true;
  } catch (err) {
    console.error('Demo sign-in failed', err);
    clearDemoKeyFromUrl();
    return false;
  }
}
